import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Radio,
  RadioGroup,
  Stack,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Progress,
  Badge,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { SurveyForm as SurveyFormAPI } from '@devd-client/api';
import type {
  Question,
  SurveyData,
  ParticipantStatus,
  ValidationResponse,
  SurveyFormResponse,
  SurveyResponse,
} from './types';
import PriorityAreasForm from './PriorityAreasForm';

const { surveyApi } = SurveyFormAPI;

const getDriverDescription = (questionKey: string) => {
  const descriptions: Record<string, string> = {
    CODE_REVIEW_PROCESS: 'How effective is the code review process?',
    DEEP_WORK: 'Can developers get uninterrupted time for focused work?',
    BUILD_PROCESSES: 'How smooth are build and deployment processes?',
    DOCUMENTATION: 'Is documentation helpful and up-to-date?',
    INCIDENT_HANDLING: 'How well are production incidents handled?',
    TECH_DEBT: 'Is technical debt managed effectively?',
    CODEBASE_EXPERIENCE:
      'How easy is it to navigate and understand the codebase?',
    CROSS_TEAM_COLLABORATION:
      'How well do teams collaborate across boundaries?',
    LOCAL_DEVELOPMENT:
      'How easy is it to set up and work in local environments?',
    TEST_COVERAGE: 'Is test coverage adequate and helpful?',
    CLEAR_DIRECTION: 'Are priorities and requirements clear?',
    REQUIREMENTS_QUALITY: 'How well-defined are feature requirements?',
    TEST_EFFICIENCY: 'How quickly and reliably testing gets done?',
    SMALL_WIPS: 'How often work is split into manageable tasks?',
    EASE_OF_RELEASE: 'How smooth and low-friction production releases feel?',
    CUSTOMER_FEEDBACK: 'How easily developers access real user insights?',
    REVIEW_TURNAROUND: 'How fast code reviews are completed?',
    TEAM_COMMUNICATION: 'How clearly and effectively the team collaborates?',
    LEARNING_OPPORTUNITIES: 'How supported growth and skill development feels?',
    ROADMAP_INPUT: 'How much input developers have on direction?',
    EXPERIMENTATION: 'How freely developers can test new ideas?',
    INTERNAL_TOOLS: 'How reliable and helpful internal tools are?',
    WORKLOAD_MANAGEMENT: 'How balanced and sustainable workload feels?',
    ONCALL_EXPERIENCE: 'How manageable and supported on-call duties are?',
    REALISTIC_DEADLINES: 'How achievable deadlines and timelines feel?',
    ROLE_DOMAIN_ENABLEMENT:
      'How would you rate the alignment between your current domain and technical knowledge and the work you are expected to deliver?',
  };
  return descriptions[questionKey] || 'Developer experience factor';
};

const SurveyForm: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [survey, setSurvey] = useState<SurveyData | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [participantStatus, setParticipantStatus] =
    useState<ParticipantStatus | null>(null);
  const [showPriorityAreas, setShowPriorityAreas] = useState(false);
  const [allCompleted, setAllCompleted] = useState(false);

  const questionsPerStep = 4; // Number of questions per step
  const totalSteps = survey
    ? Math.ceil(survey.questions.length / questionsPerStep)
    : 0;
  const currentQuestions =
    survey?.questions.slice(
      currentStep * questionsPerStep,
      (currentStep + 1) * questionsPerStep
    ) || [];

  useEffect(() => {
    if (!surveyId || !token) {
      setError('Invalid survey link');
      setLoading(false);
      return;
    }

    loadSurvey();
  }, [surveyId, token]);

  const loadSurvey = async () => {
    try {
      if (!surveyId || !token) return;

      // Step 1: Validate token access
      const validation: ValidationResponse = await surveyApi.validateToken(
        surveyId,
        token
      );
      if (!validation) {
        throw new Error('Invalid or expired survey link');
      }

      // Step 2: Fetch survey form data to get participantId
      const surveyFormData: SurveyFormResponse =
        await surveyApi.fetchSurveyQuestions(surveyId, token);

      if (!surveyFormData.participantId) {
        throw new Error('Unable to retrieve participant information');
      }

      setParticipantId(surveyFormData.participantId);

      // Step 3: Set participant status from form response
      setParticipantStatus(surveyFormData.participantStatus);

      // Step 4: Handle different statuses
      if (surveyFormData.participantStatus === 'COMPLETED') {
        setError(
          'You have already completed this survey. Thank you for your participation!'
        );
        return;
      }

      if (surveyFormData.participantStatus === 'EXPIRED') {
        setError(
          'This survey invitation has expired. Please contact the administrator if you need assistance.'
        );
        return;
      }

      if (surveyFormData.participantStatus === 'DECLINED') {
        setError('You have declined to participate in this survey.');
        return;
      }

      // Step 5: Set survey data for valid statuses
      setSurvey({
        surveyId: surveyFormData.surveyId,
        surveyTitle: surveyFormData.surveyTitle,
        description: surveyFormData.description,
        anonymous: surveyFormData.anonymous,
        estimatedTimeMinutes: surveyFormData.estimatedTimeMinutes,
        questions: surveyFormData.questions,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!surveyId || !token || !survey) return;

    // Check if participant status allows submission
    if (participantStatus === 'COMPLETED') {
      setError(
        'You have already completed this survey. Thank you for your participation!'
      );
      return;
    }

    if (participantStatus === 'EXPIRED') {
      setError(
        'This survey invitation has expired. Please contact the administrator if you need assistance.'
      );
      return;
    }

    if (participantStatus === 'DECLINED') {
      setError('You have declined to participate in this survey.');
      return;
    }

    setSubmitting(true);
    try {
      const formattedResponses = Object.entries(responses).map(
        ([questionKey, answer]) => {
          // Find the question to get its type
          const question = survey.questions.find(
            (q) => q.questionKey === questionKey
          );

          return {
            questionKey,
            answer,
          };
        }
      );

      await surveyApi.submitSurveyResponse(surveyId, token, formattedResponses);
      setSubmitted(true);
      setShowPriorityAreas(true);
    } catch (err: any) {
      setError('Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResponseChange = (questionKey: string, value: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: value,
    }));
  };

  const handlePriorityComplete = () => {
    setShowPriorityAreas(false);
    setAllCompleted(true);
  };

  const renderQuestion = (question: Question) => {
    const value = responses[question.questionKey];

    return (
      <FormControl key={question.questionKey} isRequired={question.required}>
        <FormLabel
          fontSize="lg"
          fontWeight="semibold"
          color="text.primary"
          mb={3}
        >
          <HStack spacing={2} align="center">
            <Text as="span">{question.questionText}</Text>
            <Tooltip
              hasArrow
              label={getDriverDescription(question.questionKey)}
            >
              <Box as="span" color="text.secondary" cursor="help">
                <InfoOutlineIcon boxSize={4} />
              </Box>
            </Tooltip>
          </HStack>
        </FormLabel>
        <RadioGroup
          value={value || ''}
          onChange={(val) => handleResponseChange(question.questionKey, val)}
        >
          <Stack direction="row" spacing={6} flexWrap="wrap">
            {question.optionsConfig?.options.map((option) => (
              <Radio
                key={option.value}
                value={option.value.toString()}
                size="lg"
                colorScheme="brand"
              >
                <Text fontSize="sm" ml={2}>
                  {option.label}
                </Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>
    );
  };

  const canGoNext = () => {
    const requiredQuestions = currentQuestions.filter((q) => q.required);
    return requiredQuestions.every((q) => responses[q.questionKey]);
  };

  const canSubmit = () => {
    if (!survey) return false;
    const requiredQuestions = survey.questions.filter((q) => q.required);
    return requiredQuestions.every((q) => responses[q.questionKey]);
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="primary" />
          <Text>Loading survey...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" py={20}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  if (allCompleted) {
    return (
      <Container maxW="container.md" py={20}>
        <VStack spacing={6} textAlign="center">
          <Box fontSize="6xl">✓</Box>
          <Text fontSize="2xl" fontWeight="bold" color="success">
            Thank You!
          </Text>
          <Text color="text.secondary" maxW="400px">
            Your survey response and priority areas have been submitted
            successfully. We appreciate your feedback and will use it to improve
            our development experience.
          </Text>
        </VStack>
      </Container>
    );
  }

  if (!survey) {
    return (
      <Container maxW="container.md" py={20}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Survey not found
        </Alert>
      </Container>
    );
  }

  // Show priority areas after survey completion
  if (showPriorityAreas && surveyId && token) {
    return (
      <PriorityAreasForm
        surveyId={surveyId}
        token={token}
        onComplete={handlePriorityComplete}
      />
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="container.md" py={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="text.primary">
              {survey.surveyTitle}
            </Text>
            {survey.description && (
              <Text fontSize="md" color="text.secondary" maxW="600px">
                {survey.description}
              </Text>
            )}
            <HStack spacing={4} flexWrap="wrap" justify="center">
              {survey.estimatedTimeMinutes && (
                <Text fontSize="sm" color="text.secondary">
                  Estimated time: {survey.estimatedTimeMinutes} minutes
                </Text>
              )}
              {survey.anonymous && (
                <Badge colorScheme="blue" variant="subtle">
                  Responses in this step are anonymous
                </Badge>
              )}
              {participantStatus &&
                participantStatus !== 'COMPLETED' &&
                participantStatus !== 'EXPIRED' && (
                  <Badge
                    colorScheme={
                      participantStatus === 'INVITED'
                        ? 'green'
                        : participantStatus === 'STARTED' ||
                          participantStatus === 'IN_PROGRESS'
                        ? 'blue'
                        : 'gray'
                    }
                    variant="subtle"
                  >
                    Status: {participantStatus.replace('_', ' ')}
                  </Badge>
                )}
            </HStack>
          </VStack>

          {/* Progress */}
          {totalSteps > 1 && (
            <VStack spacing={2}>
              <Progress
                value={((currentStep + 1) / totalSteps) * 100}
                colorScheme="brand"
                size="sm"
                borderRadius="full"
                bg="gray.200"
              />
              <HStack spacing={1}>
                {Array.from({ length: totalSteps }, (_, i) => (
                  <Box
                    key={i}
                    w={3}
                    h={3}
                    borderRadius="full"
                    bg={i <= currentStep ? 'primary' : 'gray.300'}
                  />
                ))}
              </HStack>
            </VStack>
          )}

          {/* Questions */}
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <VStack spacing={8} align="stretch">
              {currentQuestions.map((question, index) => (
                <Box key={question.questionKey}>
                  <HStack align="flex-start" spacing={4} mb={4}>
                    <Text
                      fontSize="lg"
                      fontWeight="medium"
                      color="text.secondary"
                      minW="30px"
                    >
                      {currentStep * questionsPerStep + index + 1}.
                    </Text>
                    <Box flex={1}>{renderQuestion(question)}</Box>
                  </HStack>
                  {index < currentQuestions.length - 1 && <Divider my={6} />}
                </Box>
              ))}
            </VStack>
          </Box>

          {/* Navigation */}
          <HStack justify="space-between">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              isDisabled={currentStep === 0}
              size="lg"
            >
              Back
            </Button>

            {currentStep < totalSteps - 1 ? (
              <Button
                colorScheme="brand"
                onClick={() => setCurrentStep(currentStep + 1)}
                isDisabled={!canGoNext()}
                size="lg"
                px={8}
              >
                Continue
              </Button>
            ) : (
              <Button
                colorScheme="brand"
                onClick={handleSubmit}
                isLoading={submitting}
                loadingText="Submitting..."
                isDisabled={!canSubmit()}
                size="lg"
                px={8}
              >
                Continue to Priority Areas
              </Button>
            )}
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default SurveyForm;
